import {
    AppBar,
    Avatar,
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Popover,
    Select,
    styled,
    TextField,
    Toolbar,
    Typography,
    useTheme,
} from '@mui/material';
import {
    ArrowRight,
    ArrowUp,
    Chats,
    Check,
    Copy,
    Gear,
    Microphone,
    Pause,
    Play,
    PlusCircle,
    SpeakerHigh,
    SpeakerSlash,
    Stop,
    Trash,
} from '@phosphor-icons/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useSearchParams } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { io, Socket } from 'socket.io-client';
import FluxPromptLogo from '../../assets/icons/fluxprompt-logo.svg';
import { IPublicChatbotModel } from '../../data/models/chatbot';
import { ChatStreamEvent } from '../../data/responses/chatbot';
import { ChatbotService } from '../../service/ChatbotService';
import { FlowService } from '../../service/FlowService';
import { FluxService } from '../../service/FluxService';
import { GroqService } from '../../service/GroqService';
import { useNotificationStore } from '../../store/storeNotifications';
import { generateRandomId } from '../../utils/generateId';
import LoadingSpinner from '../UI/LoadingSpinner';
import { ChatbotCodeComponent } from './CodeComponent';
import { LinkComponent } from './LinkComponent';
import { WaveformVisualizer } from './_components/WaveformVisualizer';

const MessageBubble = styled(Paper, {
  shouldForwardProp: prop => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  padding: '4px 16px',
  maxWidth: isUser ? '45%' : '70%',
  width: 'fit-content',
  marginBottom: theme.spacing(1),
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  borderRadius: '8px',
  wordBreak: 'break-word',
  boxShadow: 'none',
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  boxSizing: 'border-box',
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const Container = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  padding: '0 4px 86px 4px',
  margin: '0 auto',
  width: '100%',
  boxSizing: 'border-box',
  maxWidth: '768px',
  [theme.breakpoints.up('md')]: {
    padding: '0 16px 86px 16px',
  },
}));

const InputContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isEmpty',
})<{ isEmpty: boolean }>(({ theme, isEmpty }) => ({
  boxSizing: 'border-box',
  padding: '16px',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  margin: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: 'inherit',
  transition: theme.transitions.create(
    ['transform', 'position', 'top', 'bottom', 'left', 'right'],
    {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    },
  ),
  [theme.breakpoints.down('md')]: {
    margin: '0 auto'
  },
  [theme.breakpoints.up('md')]: {
    ...(!isEmpty && { margin: '0 auto' }),
    ...(isEmpty && {
      position: 'absolute',
      top: '50%',
      transform: 'translate(0%, -50%)',
      justifySelf: 'center',
    }),
  },
}));

const ConversationStarters = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '16px',
  marginBottom: '48px',
  [theme.breakpoints.down('sm')]: {
    '& h1': {
      fontSize: theme.typography.h5.fontSize,
    },
  },
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  '& .dot': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    animation: 'typing 1.4s infinite',
    '&:nth-of-type(2)': {
      animationDelay: '0.2s',
    },
    '&:nth-of-type(3)': {
      animationDelay: '0.4s',
    },
  },
  '@keyframes typing': {
    '0%, 100%': {
      transform: 'translateY(0)',
      opacity: 0.2,
    },
    '50%': {
      transform: 'translateY(-4px)',
      opacity: 1,
    },
  },
}));

const MemoizedLinkComponent = memo(LinkComponent);

const MessageContent = memo(
  ({
    content,
    messageTimestamp,
    isNewMessage,
    isChatRespondingWithVoice,
  }: {
    content: string;
    messageTimestamp: string;
    isNewMessage: boolean;
    isChatRespondingWithVoice: boolean;
  }) => {
    return (
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        remarkPlugins={[remarkGfm]}
        components={{
          img: ({ node, ...props }) => (
            <img
              {...props}
              alt={props.alt ?? 'Image'}
              className="max-w-full my-2 rounded-md"
            />
          ),
          audio: ({ node, ...props }) => (
            <>
              <audio controls className="my-2">
                <track kind="captions" />
                <source src={props.src} />
                Your browser does not support the audio element.
              </audio>
              <br />
            </>
          ),
          a: props => (
            <MemoizedLinkComponent
              {...props}
              id={messageTimestamp}
              autoPlay={isChatRespondingWithVoice}
              isNewMessage={isNewMessage}
            />
          ),
          code: props => <ChatbotCodeComponent {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>
    );
  },
);

export default function PublicChatBot() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const setNotification = useNotificationStore(state => state.setNotification);

  const session = searchParams.get('session');

  const inputRef = useRef<HTMLInputElement>(null);

  const chatAudioSpeakerRef = useRef<HTMLAudioElement>(null);

  const audioRecognitionRef = useRef<SpeechRecognition | null>(null);
  const [isRecording, setIsRecording] = useState(false);

  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const [dropdownValues, setDropdownValues] = useState<string[]>([]);

  const [isLoadingChatbot, setIsLoadingChatbot] = useState(true);

  const [socketClient, setSocketClient] = useState<Socket | null>(null);

  const [chatbot, setChatbot] = useState<IPublicChatbotModel>(
    {} as IPublicChatbotModel,
  );

  const [isReceivingContent, setIsReceivingContent] = useState(false);

  const [isGeneratingTTS, setIsGeneratingTTS] = useState(false);

  const [receivedStreamContentStorage, setReceivedStreamContentStorage] =
    useState('');

  const receivedStreamContentStorageRef = useRef(receivedStreamContentStorage);
  receivedStreamContentStorageRef.current = receivedStreamContentStorage;

  const [isLastChatResponseTTSGenerated, setIsLastChatResponseTTSGenerated] =
    useState(false);

  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(false);
  const [isChatSpeaking, setIsChatSpeaking] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'end',
      });
    }, 300);
  };

  const handleClearConversation = async () => {
    if (!chatbot) {
      setNotification({
        type: 'error',
        message: 'Chatbot not set',
      });
      return;
    }

    try {
      await FlowService.clearChatFlow(chatbot.id, session ?? '');

      setChatbot(prev => {
        return {
          ...prev,
          session: { ...prev.session, entries: [] },
        };
      });

      setNotification({
        type: 'success',
        message: 'Conversation cleared successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error clearing chat chatbot',
      });
      console.log(error);
    } finally {
      handleClose();
    }
  };

  const handleCreateNewChatSession = useCallback(async () => {
    const sessionId = generateRandomId();

    const newSession = {
      id: sessionId,
      name: sessionId,
      entries:
        chatbot.firstMessage && chatbot.storeOpeningMessage !== 'as-title'
          ? [
              {
                role: 'assistant',
                content: chatbot.firstMessage,
                timestamp: new Date().toISOString(),
                actions: chatbot.chatActions ?? [],
              },
            ]
          : [],
    };

    setChatbot(prev => ({ ...prev, session: newSession }));
    setSearchParams({ session: sessionId });
  }, [chatbot, setSearchParams]);

  const getChatbot = useCallback(async () => {
    try {
      setIsLoadingChatbot(true);
      setDropdownValues([]);

      const currentSession = session ?? generateRandomId();

      if (!session) {
        setSearchParams({ session: currentSession });
      }

      const chatbotResponse = await ChatbotService.getPublicChatbot(
        params.chatId!,
        currentSession,
      );

      const chatbotData = chatbotResponse.data;

      if (
        chatbotData.chatbotDropdowns &&
        chatbotData.chatbotDropdowns.length > 0
      ) {
        setDropdownValues(
          chatbotData.chatbotDropdowns.map(item => item.options[0].value),
        );
      }

      if (chatbotData.session) {
        setChatbot(chatbotData as any);
        return;
      }

      const newSession = {
        id: currentSession,
        name: currentSession,
        entries:
          chatbotData.firstMessage &&
          chatbotData.storeOpeningMessage !== 'as-title'
            ? [
                {
                  role: 'assistant',
                  content: chatbotData.firstMessage,
                  timestamp: new Date().toISOString(),
                  actions: chatbotData.chatActions ?? [],
                },
              ]
            : [],
      };

      chatbotData.session = newSession;

      setChatbot(chatbotData as any);
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error?.message ?? 'Error fetching chatbot',
      });
    } finally {
      setIsLoadingChatbot(false);
    }
  }, [params, setNotification, session, setSearchParams]);

  const handleSendMessage = useCallback(
    async (message?: string) => {
      if (!chatbot) {
        setNotification({
          type: 'error',
          message: 'Chatbot not set',
        });
        return;
      }

      if (!input.trim() && !message) return;

      const newMessage = {
        role: 'user',
        content: message ?? input.trim(),
        timestamp: new Date().toISOString(),
      };

      setIsTyping(true);
      setIsLastChatResponseTTSGenerated(false);

      try {
        await FluxService.chatApi(chatbot.id, socketClient?.id!, {
          sessionId: session,
          variableInputs: [
            {
              inputText: newMessage.content,
            },
            ...dropdownValues.map((value, index) => ({
              inputId: chatbot.chatbotDropdowns?.[index]?.varInputNodeId,
              inputText: value,
            })),
          ],
        });

        setChatbot(prev => ({
          ...prev,
          session: {
            ...prev.session,
            entries: [...prev.session.entries, newMessage],
          },
        }));

        setInput('');
      } catch (err) {
        console.log(err);
      } finally {
        inputRef.current?.focus();
      }
    },
    [input, chatbot, setNotification, session, socketClient, dropdownValues],
  );

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleGearClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRecord = () => {
    try {
      const SpeechRecognition =
        window.SpeechRecognition ?? window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        console.error('Speech recognition not supported in this browser');
        return;
      }

      audioRecognitionRef.current = new SpeechRecognition();
      audioRecognitionRef.current.continuous = true;
      audioRecognitionRef.current.lang = 'en-US';

      audioRecognitionRef.current.onstart = () => {
        setInput('');
        setIsRecording(true);
      };

      audioRecognitionRef.current.onend = () => {
        setIsRecording(false);
      };

      audioRecognitionRef.current.onresult = event => {
        const resultsLength = event.results.length;
        let transcript = '';

        for (let i = 0; i < resultsLength; i++) {
          const result = event.results[i];
          transcript += result[0].transcript;
        }

        setInput(transcript);
      };

      audioRecognitionRef.current.onerror = event => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      audioRecognitionRef.current.start();
    } catch (error) {
      console.error('Error initializing speech recognition:', error);
      setIsRecording(false);
    }
  };

  const handleStopRecording = () => {
    if (!audioRecognitionRef.current) return;

    audioRecognitionRef.current.stop();
    setIsRecording(false);
  };

  const handleVoiceToggle = () => {
    setIsAutoPlayEnabled(prev => !prev);
  };

  const hasAudioLink = (text: string): boolean => {
    if (text.match(/\.(mp3|wav|ogg|m4a|aac|flac|webm)/i)) {
      return true;
    }

    return false;
  };

  const generateVoiceBufferForStream = async () => {
    console.log(
      'generateVoiceBufferForStream',
      receivedStreamContentStorageRef.current,
    );

    const voiceResponse = await GroqService.voiceGenerateBuffer(
      chatbot.chatbotVoice!,
      {
        text: receivedStreamContentStorageRef.current,
        modelId: 'playai-tts',
      },
    );

    return voiceResponse;
  };

  const handleSpeak = (fileUrl: any | null) => {
    try {
      if (!fileUrl) return;

      if (chatAudioSpeakerRef.current && !isChatSpeaking) {
        const blob = new Blob([fileUrl.data], { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(blob);

        chatAudioSpeakerRef.current.src = audioUrl;
        chatAudioSpeakerRef.current.currentTime = 0;
        chatAudioSpeakerRef.current.play();
      }
    } catch (err) {
      console.log('BLOB ERROR:', err);
    }
  };

  const handleGenerateTTS = async () => {
    try {
      setIsGeneratingTTS(true);

      if (!session || chatbot.session.entries.length === 0) return null;

      const lastChatResponse =
        chatbot.session.entries[chatbot.session.entries.length - 1].content;

      const voiceResponse = await GroqService.voiceGenerateBuffer(
        chatbot.chatbotVoice!,
        {
          text: lastChatResponse,
          modelId: 'playai-tts',
        },
      );

      return voiceResponse;
    } catch (err) {
      return null;
    } finally {
      setIsGeneratingTTS(false);
    }
  };

  const handlePlayChatResponse = async () => {
    if (isChatSpeaking) {
      chatAudioSpeakerRef.current?.pause();
      return;
    }

    if (!isLastChatResponseTTSGenerated) {
      const tts = await handleGenerateTTS();

      handleSpeak(tts);
      setIsLastChatResponseTTSGenerated(true);
      return;
    }

    chatAudioSpeakerRef.current!.currentTime = 0;
    chatAudioSpeakerRef.current?.play();
  };

  // Scroll to bottom when chat or typing changes
  useEffect(() => {
    scrollToBottom();
  }, [chatbot, isTyping]);

  // Get chatbot when component mounts
  useEffect(() => {
    getChatbot();
  }, []);

  // Get chat session when chatbot changes
  useEffect(() => {
    if (chatbot.favicon) {
      const favicon = document.querySelector('link[rel="icon"]');
      favicon?.setAttribute('href', chatbot.favicon);
    }
  }, [chatbot]);

  // Socket client
  useEffect(() => {
    const socket = io(import.meta.env.VITE_API_URL as string);

    socket.on('connect', () => {
      setSocketClient(socket);
    });

    socket.on('chatbot-req', async (socketResponse: ChatStreamEvent) => {
      let generatedVoice: any = null;

      if (socketResponse.data.type === 'stream') {
        const socketData = socketResponse.data;

        switch (socketData.event) {
          case 'stream_start':
            setChatbot(prev => ({
              ...prev,
              session: {
                ...prev.session,
                entries: [
                  ...prev.session.entries,
                  {
                    role: 'assistant',
                    content: '',
                    timestamp: socketData.data.timestamp,
                  },
                ],
              },
            }));

            setIsReceivingContent(true);
            setReceivedStreamContentStorage('');
            break;
          case 'stream_error':
            break;
          case 'stream_thinking':
            break;
          case 'stream_message':
            setChatbot(prev => {
              const entryIndex = prev.session.entries.findIndex(
                entry => entry.timestamp === socketData.data.timestamp,
              );

              prev.session.entries[entryIndex].content += socketData.data.text;

              return {
                ...prev,
              };
            });

            setReceivedStreamContentStorage(old => old + socketData.data.text);
            break;
          case 'stream_end':
            if (
              isAutoPlayEnabled &&
              !hasAudioLink(receivedStreamContentStorageRef.current)
            ) {
              const voiceResponse = await generateVoiceBufferForStream();

              generatedVoice = voiceResponse;
              setIsLastChatResponseTTSGenerated(true);
            }

            setIsReceivingContent(false);
            break;
          default:
            break;
        }
      } else {
        const socketData = socketResponse.data;

        switch (socketData.event) {
          case 'message':
            if (isAutoPlayEnabled && !hasAudioLink(socketData.data[0].text)) {
              const voiceResponse = await GroqService.voiceGenerateBuffer(
                chatbot.chatbotVoice!,
                {
                  text: socketData.data[0].text,
                  modelId: 'playai-tts',
                },
              );

              generatedVoice = voiceResponse;
              setIsLastChatResponseTTSGenerated(true);
            }

            setChatbot(prev => {
              const newMessage = {
                role: 'assistant',
                content: socketData.data[0].text,
                timestamp: socketData.data[0].timestamp,
              };

              return {
                ...prev,
                session: {
                  ...prev.session,
                  entries: [...prev.session.entries, newMessage],
                },
              };
            });

            break;
          case 'error':
            setNotification({
              type: 'error',
              message: socketData.data.error,
            });
            break;
          default:
            break;
        }
      }

      handleSpeak(generatedVoice);
      setIsTyping(false);
    });

    return () => {
      socket?.off('connect');
      socket?.off('chatbot-req');
    };
  }, [isAutoPlayEnabled, session]);

  useEffect(() => {
    const audio = chatAudioSpeakerRef.current;

    if (!audio) return;

    audio.addEventListener('ended', () => {
      setIsChatSpeaking(false);
    });

    audio.addEventListener('pause', () => {
      setIsChatSpeaking(false);
    });

    audio.addEventListener('play', () => {
      setIsChatSpeaking(true);
    });

    return () => {
      audio?.removeEventListener('play', () => {});

      audio?.removeEventListener('ended', () => {});

      audio?.removeEventListener('pause', () => {});
    };
  }, [chatAudioSpeakerRef.current]);

  useEffect(() => {
    if (!isAutoPlayEnabled) {
      chatAudioSpeakerRef.current?.pause();
    }
  }, [isAutoPlayEnabled]);

  if (isLoadingChatbot) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <LoadingSpinner variant="light" />
      </Box>
    );
  }

  return (
    <>
      <audio
        key="chatbot-audio-speaker"
        ref={chatAudioSpeakerRef}
        style={{ display: 'hidden', width: 0, height: 0 }}
      />

      <ChatContainer
        sx={{
          position: 'relative',
          background: chatbot.pageBackground,
          color: chatbot.pageText,
          backgroundImage: `url(${chatbot.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          fontFamily: chatbot.font,
          fontWeight: chatbot.fontWeight,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            background: chatbot.brandColor,
            width: '100%',
            height: '4px',
            position: 'sticky',
            top: 0,
            zIndex: 1200,
            left: 0,
            right: 0,
          }}
        />

        <AppBar
          position="sticky"
          color="primary"
          elevation={0}
          sx={{
            background: chatbot.pageBackground,
          }}
        >
          <Toolbar>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    flexGrow: 1,
                    color: chatbot.pageText,
                    fontSize: '16px',
                    fontFamily: chatbot.font,
                    fontWeight: chatbot.fontWeight,
                  }}
                >
                  {chatbot.name}
                </Typography>
              </Box>

              <Divider
                orientation="vertical"
                sx={{
                  background: chatbot.pageText,
                  height: '24px',
                }}
              />

              <Box>
                <Box sx={{ maxWidth: '200px', height: '48px' }}>
                  <img
                    src={
                      chatbot.disableFluxpromptBranding
                        ? chatbot.logo ?? undefined
                        : FluxPromptLogo
                    }
                    alt="FluxPrompt"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap="12px"
            >
              {chatbot.chatbotVoice && (
                <IconButton
                  color="inherit"
                  edge="end"
                  disabled={
                    isTyping ||
                    isGeneratingTTS ||
                    isReceivingContent ||
                    chatbot.session.entries.length === 0
                  }
                  onClick={handlePlayChatResponse}
                >
                  {isChatSpeaking ? (
                    <Pause
                      size={24}
                      weight="regular"
                      color={chatbot.pageText ?? undefined}
                    />
                  ) : (
                    <Play
                      size={24}
                      weight="regular"
                      color={chatbot.pageText ?? undefined}
                    />
                  )}
                </IconButton>
              )}

              <IconButton color="inherit" edge="end" onClick={handleGearClick}>
                <Gear
                  size={24}
                  weight="regular"
                  color={chatbot.pageText ?? undefined}
                />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Container
          sx={{
            display: 'flex',
            position: 'relative',
            padding: '0 16px 86px 16px',
            margin: 0,
            [theme.breakpoints.up('md')]: {
              maxWidth: '768px',
              padding: `0 0 ${
                chatbot.disableFluxpromptBranding ? '86px' : '140px'
              } 0`,
            },
          }}
        >
          {chatbot.session && chatbot.session.entries.length > 0 && (
            <MessagesContainer>
              {chatbot.session.entries.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexDirection:
                      message.role === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  {message.role === 'assistant' && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius:
                          chatbot.iconShape === 'circle' ? '50%' : '8px',
                        padding: '4px',
                      }}
                    >
                      {chatbot.chatImage ? (
                        <Avatar
                          src={chatbot.chatImage}
                          alt={chatbot.name}
                          sx={{
                            width: `${chatbot.iconSize}px`,
                            height: `${chatbot.iconSize}px`,
                            '&.MuiAvatar-root': {
                              borderRadius:
                                chatbot.iconShape === 'circle' ? '50%' : 0,
                            },
                          }}
                        />
                      ) : (
                        <Chats
                          style={{
                            width: `${chatbot.iconSize}px`,
                            height: `${chatbot.iconSize}px`,
                            color: chatbot.pageText || '#fff',
                          }}
                        />
                      )}
                    </Box>
                  )}

                  <Box
                    id="message-bubble-container"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <MessageBubble
                      id={`message-bubble`}
                      isUser={message.role === 'user'}
                      sx={{
                        backgroundColor:
                          message.role === 'user'
                            ? chatbot.userContainer
                            : chatbot.responseContainer,
                        color:
                          message.role === 'user'
                            ? chatbot.userText
                            : chatbot.responseText,
                        fontSize: '16px',
                      }}
                    >
                      <div
                        id="message-content-father"
                        className={'chatbot-message-content'}
                      >
                        <MessageContent
                          content={message.content}
                          messageTimestamp={message.timestamp}
                          isNewMessage={
                            index === chatbot.session.entries.length - 1 &&
                            message.role === 'assistant'
                          }
                          isChatRespondingWithVoice={isTyping}
                        />
                      </div>
                    </MessageBubble>

                    {message.role !== 'user' && !message.actions && (
                      <IconButton
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'transparent',
                          width: 'fit-content',
                          minWidth: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          padding: '8px',
                          color: chatbot.responseText,
                          '&:hover': {
                            backgroundColor: chatbot.brandColor,
                            color: '#fff',
                          },
                          ...(copiedMessageId === message.timestamp && {
                            backgroundColor: chatbot.brandColor,
                            color: '#fff',
                          }),
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(message.content);
                          setCopiedMessageId(message.timestamp);

                          setTimeout(() => {
                            setCopiedMessageId(null);
                          }, 2000);
                        }}
                      >
                        {copiedMessageId === message.timestamp ? (
                          <Check size={16} weight="regular" />
                        ) : (
                          <Copy size={16} weight="regular" />
                        )}
                      </IconButton>
                    )}

                    {message.actions && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {message.actions.map(action => (
                          <Button
                            sx={{
                              background: chatbot.brandColor,
                              width: 'fit-content',
                              borderRadius: '8px',
                              padding: '8px 24px',
                              border: `1px solid ${chatbot.brandColor}`,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'start',
                              gap: 4,
                              '&:hover': {
                                background: '#fff',
                                color: chatbot.brandColor,
                              },
                            }}
                            key={action.name}
                            onClick={() =>
                              handleSendMessage(action.description)
                            }
                            disabled={isTyping}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                              }}
                            >
                              {action.name}
                            </Typography>

                            <ArrowRight size={16} />
                          </Button>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
              {isTyping && (
                <MessageBubble
                  isUser={false}
                  sx={{
                    width: 'fit-content',
                    backgroundColor: chatbot.responseContainer,
                  }}
                >
                  <TypingIndicator>
                    <span
                      className="dot"
                      style={{
                        backgroundColor: chatbot.responseText ?? undefined,
                      }}
                    />
                    <span
                      className="dot"
                      style={{
                        backgroundColor: chatbot.responseText ?? undefined,
                      }}
                    />
                    <span
                      className="dot"
                      style={{
                        backgroundColor: chatbot.responseText ?? undefined,
                      }}
                    />
                  </TypingIndicator>
                </MessageBubble>
              )}
            </MessagesContainer>
          )}
        </Container>

        <div ref={messagesEndRef} />

        <InputContainer
          sx={{
            maxWidth: '768px',
            background: chatbot.backgroundImage
              ? 'transparent'
              : chatbot.pageBackground,
            zIndex: 10,
          }}
          isEmpty={chatbot.session?.entries.length === 0 && !isTyping}
        >
          {chatbot.session?.entries.length === 0 && !isTyping && (
            <ConversationStarters>
              <Box
                sx={{
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '16px',
                  padding: '5px',
                  [theme.breakpoints.up('md')]: {
                    width: '100px',
                    height: '100px',
                  },
                }}
              >
                {chatbot.chatImage ? (
                  <Avatar
                    src={chatbot.chatImage}
                    alt={chatbot.name}
                    sx={{
                      width: '100%',
                      height: '100%',

                      '&.MuiAvatar-root': {
                        borderRadius: 0,
                      },
                    }}
                  />
                ) : (
                  <Chats
                    size={50}
                    style={{
                      color: chatbot.pageText ?? '#fff',
                    }}
                  />
                )}
              </Box>

              <Typography
                variant="body1"
                sx={{
                  fontSize: {
                    md: '32px',
                    sm: '24px',
                  },
                  lineHeight: '133%',
                  color: chatbot.pageText,
                  fontWeight: 'bold',
                }}
              >
                {chatbot.storeOpeningMessage === 'as-title' &&
                chatbot.firstMessage
                  ? chatbot.firstMessage
                  : chatbot.name}
              </Typography>

              {chatbot.storeOpeningMessage === 'as-title' &&
                chatbot.chatActions &&
                chatbot.chatActions.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      mt: 2,
                      width: '100%',
                      maxWidth: '768px',
                    }}
                  >
                    {chatbot.chatActions.map(action => (
                      <Button
                        sx={{
                          background: chatbot.brandColor,
                          width: '100%',
                          borderRadius: '8px',
                          padding: '8px 24px',
                          border: `1px solid ${chatbot.brandColor}`,
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          '&:hover': {
                            background: '#fff',
                            color: chatbot.brandColor,
                          },
                        }}
                        key={action.name}
                        onClick={() => handleSendMessage(action.description)}
                        disabled={isTyping}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                          }}
                        >
                          {action.name}
                        </Typography>
                        <ArrowRight size={16} />
                      </Button>
                    ))}
                  </Box>
                )}
            </ConversationStarters>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
              height: 'auto',
              background: chatbot.responseContainer,
              borderRadius: '8px',
              border: `1px solid ${chatbot.inputBorderColor}`,
              boxShadow: '10px 10px 40px 0px #0000001A',
              [theme.breakpoints.up('md')]: {
                maxWidth: '768px',
              },
            }}
          >
            {isRecording ? (
              <WaveformVisualizer
                handleStopRecording={handleStopRecording}
                isRecording={isRecording}
                backgroundColor={chatbot.responseContainer ?? '#fff'}
                waveColor={chatbot.brandColor ?? '#000'}
              />
            ) : (
              <>
                <TextField
                  ref={inputRef}
                  fullWidth
                  variant="outlined"
                  placeholder={chatbot.placeholder ?? 'Type your message...'}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  multiline
                  maxRows={4}
                  InputProps={{
                    sx: {
                      color: chatbot.responseText,
                      width: '100%',
                      height: '100%',
                      fontFamily: chatbot.font,
                      fontWeight: chatbot.fontWeight,
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={handleRecord}
                          disabled={isLoadingChatbot || isTyping}
                        >
                          {isRecording ? (
                            <Stop color="red" size={20} weight="regular" />
                          ) : (
                            <Microphone
                              style={{ color: chatbot.brandColor ?? 'primary' }}
                              size={20}
                              weight="regular"
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          onClick={() => handleSendMessage()}
                          disabled={!input.trim()}
                          sx={{
                            margin: 0,
                            padding: 0,
                            minWidth: 0,
                            minHeight: 0,
                            background: chatbot.brandColor,
                            width: '24px',
                            height: '24px',
                            '&:hover': {
                              background: chatbot.brandColor,
                            },
                          }}
                        >
                          <ArrowUp size={16} weight="regular" />
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    background: chatbot.responseContainer,
                    color: chatbot.responseText,
                    borderRadius: '8px',
                    width: '100%',
                    '& .MuiInputBase-root:hover fieldset': {
                      borderColor: chatbot.inputBorderColor,
                    },
                    '& .MuiInputBase-root:focus fieldset': {
                      borderColor: chatbot.brandColor,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: chatbot.brandColor,
                    },
                    '& .MuiInputBase-root fieldset': {
                      borderColor: chatbot.inputBorderColor,
                    },
                  }}
                />

                {chatbot.chatbotDropdowns &&
                  chatbot.chatbotDropdowns.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 4,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '4px',
                        padding: '0 14px',
                      }}
                    >
                      {chatbot.chatbotDropdowns?.map((item, index) => (
                        <Select
                          sx={{
                            background: chatbot.responseContainer,
                            color: chatbot.responseText,
                            '& fieldset': {
                              border: 'none',
                            },
                          }}
                          label={item.name}
                          key={item.name}
                          value={dropdownValues[index]}
                          onChange={e => {
                            setDropdownValues(prev => {
                              const newValues = [...prev];
                              newValues[index] = e.target.value;
                              return newValues;
                            });
                          }}
                        >
                          {item.options.map(option => (
                            <MenuItem value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ))}
                    </Box>
                  )}
              </>
            )}
          </Box>

          {!chatbot.disableFluxpromptBranding && (
            <Typography
              sx={{
                color: chatbot.pageText,
                textAlign: 'center',
                mt: '24px',
                pb: '24px',
                display: 'none',
                fontSize: '14px',
                fontFamily: chatbot.font,
                fontWeight: chatbot.fontWeight,
                [theme.breakpoints.up('md')]: {
                  display: 'block',
                },
              }}
            >
              Subscribe to create your own chatbot.
            </Typography>
          )}
        </InputContainer>
      </ChatContainer>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List>
          <ListItemButton
            onClick={handleCreateNewChatSession}
            disabled={!chatbot.session?.entries.length}
          >
            <ListItemIcon>
              <PlusCircle size={20} />
            </ListItemIcon>

            <ListItemText sx={{ fontSize: '14px' }} primary="Create New Chat" />
          </ListItemButton>

          <ListItemButton
            onClick={handleClearConversation}
            disabled={!chatbot.session?.entries.length}
          >
            <ListItemIcon>
              <Trash size={20} />
            </ListItemIcon>

            <ListItemText
              sx={{ fontSize: '14px' }}
              primary="Clear Conversation"
            />
          </ListItemButton>

          {chatbot.chatbotVoice && (
            <ListItemButton onClick={handleVoiceToggle}>
              <ListItemIcon>
                {isAutoPlayEnabled ? (
                  <SpeakerHigh size={20} weight="regular" />
                ) : (
                  <SpeakerSlash size={20} />
                )}
              </ListItemIcon>

              <ListItemText
                sx={{ fontSize: '14px' }}
                primary={`Autoplay Audio ${
                  isAutoPlayEnabled ? 'Enabled' : 'Disabled'
                }`}
              />
            </ListItemButton>
          )}
        </List>
      </Popover>
    </>
  );
}
